// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { CircleHelp, Eye, EyeOff, TriangleAlert } from "lucide-react";
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useEditStoreMutation, useStoreDetailQuery } from "@/api/hooks/use-stores";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { cn } from "@/lib/utils";

// The WiFi username only applies to enterprise (EAP) auth, so it's a base-optional field and is
// required conditionally — only when the selected auth mode is enterprise. `getEnterpriseAuthModeIds`
// is read at validation time (via a ref) so it stays correct as the auth-mode context loads and as the
// user changes the mode. Everything else is unconditional.
function makeHardwareSchema(getEnterpriseAuthModeIds: () => Set<number>) {
  // The backend returns `null` (not absent) for unset optional fields, and Zod's `.optional()` only
  // permits `undefined` — so these must be `.nullish()` (null OR undefined), otherwise a null value
  // silently fails validation on a possibly-hidden field and Save no-ops (CIN-671).
  return z
    .object({
      fieldSettings: z.object({
        otaVersion: z.string().min(1, "Required"),
        sc25OtaVersion: z.string().nullish(),
        // Per component: { [cpuType]: { [firmwareType]: version } }. The component set comes from the
        // firmware catalogue, so the keys are not known ahead of time.
        sc25OtaComponents: z.record(z.string(), z.record(z.string(), z.string().nullable())).nullish(),
        // One-shot instructions about how to apply this save rather than stored settings
        forceUnfreezeDevices: z.boolean().nullish(),
        overrideChainVersion: z.boolean().nullish(),
        remoteUrl: z.string().nullish(),
        deploymentInterval: z.number().nullish(),
        jpgQuality: z.number().min(0).max(100).nullish(),
        frameSize: z.number().nullish(),
        streamingTime: z.number().nullish(),
        streamingSize: z.number().nullish(),
        autoWhiteBalance: z.boolean().nullish(),
        wifiSettings: z.object({
          authMode: z.number(),
          ssid: z.string(),
          password: z.string(),
          username: z.string().nullish(),
          timeout: z.number().nullish(),
        }),
      }),
    })
    .superRefine((values, ctx) => {
      const wifi = values.fieldSettings.wifiSettings;
      if (getEnterpriseAuthModeIds().has(wifi.authMode) && !wifi.username?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["fieldSettings", "wifiSettings", "username"],
        });
      }
    });
}

type HardwareValues = z.infer<ReturnType<typeof makeHardwareSchema>>;

export interface StoreHardwareTabHandle {
  reset: () => void;
}

interface StoreHardwareTabProps {
  storeName: string;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<StoreHardwareTabHandle>;
}

const DIRTY_HIGHLIGHT = "bg-sidebar-accent-hover transition-colors";

// Build valid form values from a loaded store. The store-detail API returns the WiFi fields as
// optional, but the form schema (and the edit endpoint) require them — so a sparsely-populated store
// would seed the form with `undefined`s and fail validation on hidden fields, leaving Save silently
// dead (CIN-671). Coalesce the missing values so the form always starts in a submittable state.
function toHardwareFormValues(fieldSettings: HardwareValues["fieldSettings"]): HardwareValues {
  const wifi = fieldSettings.wifiSettings;
  return {
    fieldSettings: {
      ...fieldSettings,
      sc25OtaVersion: fieldSettings.sc25OtaVersion ?? null,
      // Seeded from the store's current assignment per component, so a component left alone resubmits
      // the version it already has instead of being cleared
      sc25OtaComponents: fieldSettings.sc25OtaComponents ?? {},
      // Always start unchecked: these instruct this one save, they are not persisted state
      forceUnfreezeDevices: false,
      overrideChainVersion: false,
      wifiSettings: {
        authMode: wifi?.authMode ?? 0,
        ssid: wifi?.ssid ?? "",
        password: wifi?.password ?? "",
        username: wifi?.username,
        timeout: wifi?.timeout,
      },
    },
  };
}

export function StoreHardwareTab({ storeName, onDirtyChange, ref }: StoreHardwareTabProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.storesHardwareEdit);
  const { data: storeRaw } = useStoreDetailQuery(storeName);
  const { data: context } = useBackofficeContext();
  const editStore = useEditStoreMutation(storeName);

  const store = storeRaw as unknown as (HardwareValues & Record<string, unknown>) | undefined;

  const emptyDefaults: HardwareValues = {
    fieldSettings: {
      otaVersion: "",
      sc25OtaVersion: null,
      sc25OtaComponents: {},
      forceUnfreezeDevices: false,
      overrideChainVersion: false,
      wifiSettings: { authMode: 0, ssid: "", password: "" },
    },
  };

  // Auth modes whose label marks them enterprise (EAP) — those require a WiFi username. Kept in a ref
  // so the once-built schema reads the current set even though the context loads asynchronously.
  const enterpriseAuthModeIds = useMemo(
    () =>
      new Set(
        (context?.wifiAuthModes ?? [])
          .filter((pair) => /eap|enterprise/i.test(pair.value))
          .map((pair) => Number(pair.id)),
      ),
    [context?.wifiAuthModes],
  );
  const enterpriseAuthModeIdsRef = useRef(enterpriseAuthModeIds);
  enterpriseAuthModeIdsRef.current = enterpriseAuthModeIds;
  const [schema] = useState(() => makeHardwareSchema(() => enterpriseAuthModeIdsRef.current));

  const form = useForm<HardwareValues>({
    resolver: zodResolver(schema),
    defaultValues: store ? toHardwareFormValues(store.fieldSettings) : emptyDefaults,
  });

  useEffect(
    function resetFormWhenStoreDataLoads() {
      if (store) {
        form.reset(toHardwareFormValues(store.fieldSettings));
      }
    },
    [store, form],
  );

  async function onSubmit(values: HardwareValues) {
    try {
      // The edit request types its optionals as `T | undefined` (not null), so coalesce the nullish
      // form values back to undefined before sending.
      const fs = values.fieldSettings;
      const fieldSettings = {
        ...fs,
        sc25OtaVersion: fs.sc25OtaVersion ?? undefined,
        remoteUrl: fs.remoteUrl ?? undefined,
        deploymentInterval: fs.deploymentInterval ?? undefined,
        jpgQuality: fs.jpgQuality ?? undefined,
        frameSize: fs.frameSize ?? undefined,
        streamingTime: fs.streamingTime ?? undefined,
        streamingSize: fs.streamingSize ?? undefined,
        autoWhiteBalance: fs.autoWhiteBalance ?? undefined,
        sc25OtaComponents: fs.sc25OtaComponents ?? undefined,
        // Only sent when ticked, so an untouched save carries no instruction at all
        forceUnfreezeDevices: fs.forceUnfreezeDevices || undefined,
        overrideChainVersion: fs.overrideChainVersion || undefined,
        wifiSettings: {
          ...fs.wifiSettings,
          username: fs.wifiSettings.username ?? undefined,
          timeout: fs.wifiSettings.timeout ?? undefined,
        },
      };
      await editStore.mutateAsync({ hardware: { fieldSettings } });
      toast.success(
        t("stores.hardware.notifications.updated", {
          name: (store as any)?.legibleName ?? storeName,
        }),
      );
      // Reset to the form's own current values so dirty state clears on 200 (independent of refetch)
      form.reset(form.getValues());
    } catch {
      // Error toast handled by API middleware
    }
  }

  const { isDirty, dirtyFields } = form.formState;

  useImperativeHandle(ref, () => ({ reset: () => form.reset() }));

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(
    function notifyParentOfDirtyState() {
      onDirtyChangeRef.current?.(isDirty);
    },
    [isDirty],
  );

  const wifiAuthOptions = (context?.wifiAuthModes ?? []).map((pair) => ({
    label: pair.value,
    value: String(pair.id),
  }));
  const otaVersionOptions = (context?.otaVersions ?? []).map((pair) => ({
    label: pair.value,
    value: pair.value,
  }));

  // One field per SC25 OTA component. An SC25 runs two CPUs and its T41 accepts either a full image or a
  // single partition such as the calibration file, each assigned independently — so they are separate
  // fields and several can be set in one save. The set follows the firmware catalogue, so a version
  // registered for a new cpu or partition gets its own field with no code change.
  const sc25Components = useMemo(
    () =>
      Object.entries(context?.sc25OtaComponents ?? {}).flatMap(([cpuType, firmwareTypes]) =>
        Object.entries(firmwareTypes).map(([firmwareType, versions]) => ({
          cpuType,
          firmwareType,
          name: `fieldSettings.sc25OtaComponents.${cpuType}.${firmwareType}` as const,
          options: versions.map((pair) => ({ label: pair.value, value: pair.value })),
        })),
      ),
    [context?.sc25OtaComponents],
  );

  // react-hook-form types a record's dirty state as boolean-or-nested, so narrow before indexing it
  function isComponentDirty(cpuType: string, firmwareType: string): boolean {
    const componentsDirty = dirtyFields.fieldSettings?.sc25OtaComponents;
    if (!componentsDirty || typeof componentsDirty === "boolean") {
      return !!componentsDirty;
    }
    return !!componentsDirty[cpuType]?.[firmwareType];
  }

  // Overriding only means something when the applied firmware was inherited from the chain, so the
  // choice is offered only then — matching the field tool
  const isChainAssigned = !!(storeRaw as { fieldSettings?: { isChainOta?: boolean } } | undefined)?.fieldSettings
    ?.isChainOta;

  const jpgQuality = form.watch("fieldSettings.jpgQuality");

  // No OTA version selected yet (e.g. a freshly created / empty store) — prompt for it. Watches the
  // live form value so the warning clears the moment the user picks one (the OTA field also shows
  // its own "Required" error on save).
  const isMissingData = !!storeRaw && !form.watch("fieldSettings.otaVersion");

  const currentAuthMode = form.watch("fieldSettings.wifiSettings.authMode");
  const currentAuthLabel = wifiAuthOptions.find((o) => o.value === String(currentAuthMode))?.label?.toUpperCase() ?? "";
  const isOpenAuth = currentAuthLabel.includes("OPEN");
  const isEnterpriseAuth = currentAuthLabel.includes("EAP") || currentAuthLabel.includes("ENTERPRISE");
  const passwordFieldVisible = !isOpenAuth;
  const usernameFieldVisible = isEnterpriseAuth;

  const [isPasswordRevealed, setIsPasswordRevealed] = useState(false);

  const sc25OtaOptions = (context?.sc25OtaVersions ?? []).map((pair) => ({
    label: pair.value,
    value: pair.value,
  }));

  return (
    <div className="max-w-2xl space-y-6 p-6">
      {isMissingData && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{t("stores.missingDataWarning")}</span>
        </div>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="fieldSettings.remoteUrl"
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel>{t("stores.hardware.fields.remoteUrl")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      className={cn(dirtyFields.fieldSettings?.remoteUrl && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fieldSettings.deploymentInterval"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.hardware.fields.deploymentInterval")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                      className={cn(dirtyFields.fieldSettings?.deploymentInterval && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fieldSettings.streamingTime"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.hardware.fields.streamingTime")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                      className={cn(dirtyFields.fieldSettings?.streamingTime && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormItem
              className={cn(
                "col-span-2 rounded-md p-2 pb-4 -m-2 -mb-2",
                dirtyFields.fieldSettings?.jpgQuality && DIRTY_HIGHLIGHT,
              )}
            >
              <div className="flex items-baseline justify-between">
                <FormLabel>{t("stores.hardware.fields.jpgQuality")}</FormLabel>
                <span className="font-mono text-sm tabular-nums text-muted-foreground">{jpgQuality ?? "—"}</span>
              </div>
              <FormControl>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={[jpgQuality ?? 0]}
                  onValueChange={(values) => {
                    form.setValue("fieldSettings.jpgQuality", values[0] ?? 0, {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                />
              </FormControl>
            </FormItem>
            <FormField
              control={form.control}
              name="fieldSettings.autoWhiteBalance"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    "flex items-center justify-between rounded-lg border p-3",
                    dirtyFields.fieldSettings?.autoWhiteBalance && DIRTY_HIGHLIGHT,
                  )}
                >
                  <FormLabel className="font-normal">{t("stores.hardware.fields.autoWhiteBalance")}</FormLabel>
                  <FormControl>
                    <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <Separator />

          <h3 className="text-base font-semibold">{t("stores.hardware.firmwareSection")}</h3>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="fieldSettings.otaVersion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.hardware.fields.otaVersion")}</FormLabel>
                  <FormControl>
                    <VirtualSelectPopover
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      options={otaVersionOptions}
                      placeholder={t("stores.hardware.selectOtaVersion")}
                      searchPlaceholder={t("stores.operations.searchOption")}
                      emptyText={t("stores.operations.noOption")}
                      className={cn(dirtyFields.fieldSettings?.otaVersion && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fieldSettings.sc25OtaVersion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.hardware.fields.sc25OtaVersion")}</FormLabel>
                  <FormControl>
                    <VirtualSelectPopover
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      options={sc25OtaOptions}
                      placeholder={t("stores.hardware.selectSc25OtaVersion")}
                      searchPlaceholder={t("stores.operations.searchOption")}
                      emptyText={t("stores.operations.noOption")}
                      className={cn(dirtyFields.fieldSettings?.sc25OtaVersion && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {sc25Components.map((component) => (
              <FormField
                key={component.name}
                control={form.control}
                name={component.name}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("stores.hardware.fields.sc25OtaComponentVersion", {
                        cpuType: component.cpuType,
                        firmwareType: component.firmwareType,
                      })}
                    </FormLabel>
                    <FormControl>
                      <VirtualSelectPopover
                        value={(field.value as string | undefined) ?? ""}
                        onChange={field.onChange}
                        options={component.options}
                        placeholder={t("stores.hardware.selectSc25OtaVersion")}
                        searchPlaceholder={t("stores.operations.searchOption")}
                        emptyText={t("stores.operations.noOption")}
                        className={cn(isComponentDirty(component.cpuType, component.firmwareType) && DIRTY_HIGHLIGHT)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>

          {/* How this save is applied, rather than stored settings — mirrors the field tool's OTA dialog */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="fieldSettings.forceUnfreezeDevices"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="flex items-center gap-1.5">
                    <FormLabel className="font-normal">{t("stores.hardware.fields.forceUnfreezeDevices")}</FormLabel>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center text-muted-foreground">
                          <CircleHelp className="h-4 w-4" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">{t("stores.hardware.forceUnfreezeDevicesHint")}</TooltipContent>
                    </Tooltip>
                  </div>
                  <FormControl>
                    <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            {isChainAssigned && (
              <FormField
                control={form.control}
                name="fieldSettings.overrideChainVersion"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <FormLabel className="font-normal">{t("stores.hardware.fields.overrideChainVersion")}</FormLabel>
                    <FormControl>
                      <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          </div>

          <Separator />

          <h3 className="text-base font-semibold">{t("stores.hardware.networkSection")}</h3>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="fieldSettings.wifiSettings.ssid"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.hardware.fields.ssid")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className={cn(dirtyFields.fieldSettings?.wifiSettings?.ssid && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fieldSettings.wifiSettings.authMode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.hardware.fields.authMode")}</FormLabel>
                  <FormControl>
                    <VirtualSelectPopover
                      value={String(field.value)}
                      onChange={(v) => field.onChange(Number(v))}
                      options={wifiAuthOptions}
                      placeholder={t("stores.hardware.selectAuthMode")}
                      searchPlaceholder={t("stores.operations.searchOption")}
                      emptyText={t("stores.operations.noOption")}
                      className={cn(dirtyFields.fieldSettings?.wifiSettings?.authMode && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {usernameFieldVisible && (
              <FormField
                control={form.control}
                name="fieldSettings.wifiSettings.username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("stores.hardware.fields.username")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        className={cn(dirtyFields.fieldSettings?.wifiSettings?.username && DIRTY_HIGHLIGHT)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {passwordFieldVisible && (
              <FormField
                control={form.control}
                name="fieldSettings.wifiSettings.password"
                render={({ field }) => (
                  <FormItem className={cn(!usernameFieldVisible && "col-span-2")}>
                    <FormLabel>{t("stores.hardware.fields.password")}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={isPasswordRevealed ? "text" : "password"}
                          {...field}
                          className={cn("pr-9", dirtyFields.fieldSettings?.wifiSettings?.password && DIRTY_HIGHLIGHT)}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
                          onClick={() => setIsPasswordRevealed((prev) => !prev)}
                          tabIndex={-1}
                        >
                          {isPasswordRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="fieldSettings.wifiSettings.timeout"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.hardware.fields.timeout")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                      className={cn(dirtyFields.fieldSettings?.wifiSettings?.timeout && DIRTY_HIGHLIGHT)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {isDirty && (
            <SaveDiscardPane onDiscard={() => form.reset()} saving={editStore.isPending} saveDisabled={!canEdit} />
          )}
        </form>
      </Form>
    </div>
  );
}
